'use client';
import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  X,
  CheckCircle2,
  AlertCircle,
  Clock,
  TrendingUp,
  Trash2,
} from 'lucide-react';
import {
  markNotificationAsRead,
  markAllNotificationsAsRead,
  removeNotification,
  fetchNotifications,
  markNotificationReadRemote,
  markAllNotificationsReadRemote,
} from '../redux/notificationsSlice';
import { Button } from './ui/Button';
import { cn } from '../utils/cn';

// How often to re-poll persisted notifications (lead/deal/task/ticket
// assignment, etc.) — there is no real-time socket event for these today,
// so a periodic fetch is what actually gets an assignment notice in front
// of a user instead of it sitting unseen in the database.
const POLL_INTERVAL_MS = 60000;

export default function NotificationBell() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { notifications, unreadCount, criticalCount } = useSelector(
    (state) => state.notifications
  );
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all', 'critical', 'unread'
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    dispatch(fetchNotifications());
    const interval = setInterval(() => dispatch(fetchNotifications()), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [dispatch]);

  const filteredNotifications = notifications.filter((notif) => {
    if (filter === 'critical') return notif.type === 'critical';
    if (filter === 'unread') return !notif.read;
    return true;
  });

  // Persisted (backend) notifications need the read-state change to actually
  // reach the server — the local-only reducer alone previously left every
  // fetched notification permanently "unread" from the backend's perspective.
  const handleMarkAsRead = (notif) => {
    dispatch(markNotificationAsRead(notif.id));
    if (notif.persisted) dispatch(markNotificationReadRemote(notif.id));
  };

  const handleMarkAllAsRead = () => {
    dispatch(markAllNotificationsAsRead());
    dispatch(markAllNotificationsReadRemote());
  };

  const handleRemove = (notifId) => {
    dispatch(removeNotification(notifId));
  };

  const handleOpen = (notif) => {
    if (!notif.read) handleMarkAsRead(notif);
    if (notif.link) {
      setIsOpen(false);
      navigate(notif.link);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'critical':
        return <AlertCircle size={16} className="text-[#BA0517]" />;
      case 'success':
        return <CheckCircle2 size={16} className="text-[#2E844A]" />;
      case 'info':
        return <TrendingUp size={16} className="text-[#0176D3]" />;
      case 'warning':
        return <Clock size={16} className="text-[#DD7A01]" />;
      default:
        return <Bell size={16} className="text-[#5C5C5C]" />;
    }
  };

  const getNotificationBgColor = (type) => {
    switch (type) {
      case 'critical':
        return 'bg-[#FDE7E9] border-[#F6A3AB]';
      case 'success':
        return 'bg-[#E3F3E8] border-[#9FD9B4]';
      case 'warning':
        return 'bg-[#FFF4E5] border-[#FFB75D]';
      default:
        return 'bg-[#EAF4FE] border-[#B8DAF3]';
    }
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return then.toLocaleDateString('en-IN');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-[#F4F6F9] text-[#706E6B] hover:text-[#0176D3] transition-colors"
        aria-label="Notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[10px] font-bold text-white bg-[#BA0517]">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-[min(384px,calc(100vw-2rem))] bg-white border border-[#D8DDE6] rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] z-50">
          {/* Header */}
          <div className="px-4 py-3 border-b border-[#D8DDE6] bg-[#F4F6F9] flex items-center justify-between">
            <div>
              <div className="text-[13px] font-bold text-[#080707]">Notifications</div>
              {criticalCount > 0 && (
                <div className="text-[11px] text-[#BA0517] font-medium">
                  {criticalCount} critical alert{criticalCount !== 1 ? 's' : ''}
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              icon={X}
              onClick={() => setIsOpen(false)}
            />
          </div>

          {/* Filter Tabs */}
          <div className="px-4 py-3 border-b border-[#D8DDE6] flex items-center gap-2">
            {['all', 'critical', 'unread'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={cn(
                  'px-3 py-1.5 text-[11px] font-bold rounded-[4px] transition-colors',
                  filter === f
                    ? 'bg-[#0176D3] text-white'
                    : 'bg-[#F4F6F9] text-[#706E6B] hover:bg-[#E8EEF5]'
                )}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
                {f === 'critical' && criticalCount > 0 && (
                  <span className="ml-1 font-bold">({criticalCount})</span>
                )}
                {f === 'unread' && unreadCount > 0 && (
                  <span className="ml-1 font-bold">({unreadCount})</span>
                )}
              </button>
            ))}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {filteredNotifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <div className="w-12 h-12 rounded-full bg-[#F4F6F9] mx-auto mb-3 flex items-center justify-center">
                  <Bell size={20} className="text-[#706E6B]" />
                </div>
                <div className="text-[12px] font-medium text-[#706E6B]">
                  {filter === 'critical'
                    ? 'No critical alerts'
                    : filter === 'unread'
                    ? 'No unread notifications'
                    : 'No notifications yet'}
                </div>
              </div>
            ) : (
              <div className="divide-y divide-[#D8DDE6]">
                {filteredNotifications.map((notif) => (
                  <div
                    key={notif.id}
                    onClick={() => handleOpen(notif)}
                    className={cn(
                      'px-4 py-3 hover:bg-[#F8FAFC] transition-colors border-l-4',
                      notif.link && 'cursor-pointer',
                      getNotificationBgColor(notif.type),
                      notif.type === 'critical' ? 'border-l-red-600' : 'border-l-[#0176D3]',
                      notif.read ? 'opacity-75' : 'opacity-100'
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 shrink-0">
                        {getNotificationIcon(notif.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="text-[12px] font-bold text-[#080707]">
                            {notif.title}
                          </div>
                          {!notif.read && (
                            <div className="w-2 h-2 rounded-full bg-[#0176D3] shrink-0 mt-1" />
                          )}
                        </div>
                        <div className="text-[11px] text-[#706E6B] mt-1">
                          {notif.message}
                        </div>
                        {notif.metrics && (
                          <div className="mt-2 text-[10px] text-[#706E6B] space-y-1">
                            {notif.metrics.sentCount !== undefined && (
                              <div>📤 Sent: {notif.metrics.sentCount}</div>
                            )}
                            {notif.metrics.deliveredCount !== undefined && (
                              <div>✓ Delivered: {notif.metrics.deliveredCount}</div>
                            )}
                            {notif.metrics.failedCount !== undefined && (
                              <div>✗ Failed: {notif.metrics.failedCount}</div>
                            )}
                            {notif.metrics.deliveryRate !== undefined && (
                              <div>📊 Rate: {(notif.metrics.deliveryRate * 100).toFixed(1)}%</div>
                            )}
                          </div>
                        )}
                        <div className="text-[10px] text-[#706E6B] mt-2">
                          {formatTime(notif.timestamp)}
                        </div>
                      </div>
                    </div>

                    {/* Notification Actions */}
                    <div className="flex items-center gap-2 mt-2 ml-7">
                      {!notif.read && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleMarkAsRead(notif); }}
                          className="text-[10px] font-medium text-[#0176D3] hover:underline"
                        >
                          Mark read
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRemove(notif.id); }}
                        className="text-[10px] font-medium text-[#706E6B] hover:text-[#BA0517]"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {filteredNotifications.length > 0 && unreadCount > 0 && (
            <div className="px-4 py-3 border-t border-[#D8DDE6] bg-[#F8FAFC] text-center">
              <button
                onClick={handleMarkAllAsRead}
                className="text-[12px] font-bold text-[#0176D3] hover:text-[#005BA3]"
              >
                Mark all as read
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
